<script lang="js">
import axios from "axios";
import Swal from "sweetalert2";

export default {
  data() {
    return {
      logger: null,
      applicationNumber: "",
      password: "",
      rememberMe: false,
      showPassword: false,
      loading: false,
      errorMessage: "",
    };
  },
  created() {
    // Create a logger instance for this component
    this.logger = this.$createLogger('AppLoginView');
  },
  methods: {
    togglePassword() {
      this.showPassword = !this.showPassword;
    },
    async login() {
      this.loading = true;
      this.errorMessage = "";

      try {
        const apiUrl = `${process.env.VUE_APP_API_BASE_URL}/auth/login`;

        const response = await axios.post(apiUrl,
          {
            identifier: this.applicationNumber,
            password: this.password,
            rememberMe: this.rememberMe, // Send remember me flag
          },
          {
            headers: {
              "Content-Type": "application/json",
            },
            withCredentials: true, // Include cookies
          }
        );

        if (response.data.success && response.data.data) {
          if (response.data.data.context?.user?.role === "staff") {
            await axios.post(
              `${process.env.VUE_APP_API_BASE_URL}/auth/logout`,
              {},
              { withCredentials: true }
            );
            throw new Error("Staff accounts must sign in through the staff portal");
          }
          // Store user data in Vuex store
          this.$store.commit("setSession", response.data.data.context);

          // Store access token in Vuex and set it in axios headers
          if (response.data.data.accessToken) {
            this.$store.commit("setAccessToken", response.data.data.accessToken);
          }

          // Mark auth as checked so router guard doesn't re-verify
          this.$store.commit("setAuthChecked", true);

          await Swal.fire({
            title: "Success!",
            text: "Login successful!",
            icon: "success",
            timer: 1500,
            showConfirmButton: false,
            timerProgressBar: true
          });

          // Navigate to dashboard using Vue Router (works in both dev and production)
          // In dev: uses router/index.js which has { name: 'app' } at /app
          // In prod: uses router/portal.js which has { name: 'app' } at /dashboard
          this.$router.push({ name: "app" });
        } else {
          this.errorMessage = "Invalid login credentials. Please check your application number and password.";
          Swal.fire({
            icon: "error",
            title: "Login Failed",
            text: "Invalid login credentials. Please check your application number and password."
          });
        }
      } catch (error) {
        this.logger.error("Login Error:", error);
        this.errorMessage =
          error.response?.data?.message ||
          error.message ||
          "Login failed. Please try again later.";
        Swal.fire({
          icon: "error",
          title: "Login Failed",
          text: this.errorMessage
        });
      } finally {
        this.loading = false;
      }
    },
  },
  components: {},
};
</script>

<template>
  <div class="container-fluid bg-body-tertiary">
    <div class="d-flex align-items-center py-4 vh-100">
      <main class="form-signin w-100 m-auto">
        <form @submit.prevent="login">
          <!-- <RouterLink to="/"> -->
          <img
            src="@/assets/logo.png"
            class="mb-4"
            alt=""
            width="96.26"
            height="70"
          />
          <!-- </RouterLink> -->
          <h1 class="h3 mb-3 fw-bold text-uppercase">FCP Login</h1>
          <p>Please fill out the following fields to login</p>

          <div
            v-if="errorMessage"
            class="alert alert-danger alert-dismissible fade show mt-3 d-flex justify-content-between"
            role="alert"
          >
            <div>
              <i class="bi bi-exclamation-triangle-fill me-2"></i>
              {{ errorMessage }}
            </div>

            <button
              type="button"
              class="btn-close"
              data-bs-dismiss="alert"
              aria-label="Close"
            ></button>
          </div>

          <div class="form-floating">
            <input
              type="text"
              class="form-control border border-bottom-0 rounded-bottom-0"
              v-model="applicationNumber"
              id="floatingInput"
              placeholder="FC24123456"
              required
            />
            <label for="floatingInput"
              >Application or Matriculation Number</label
            >
          </div>
          <div class="input-group">
            <div class="form-floating">
              <input
                :type="showPassword ? 'text' : 'password'"
                class="form-control mb-0 border-end-0"
                id="floatingPassword"
                v-model="password"
                placeholder="Password"
                required
              />
              <label for="floatingPassword">Password</label>
            </div>

            <i
              :class="showPassword ? 'bi bi-eye-slash' : 'bi bi-eye'"
              class="text-muted input-group-text bg-white border-start-0 rounded-top-0"
              id="pwd-addon"
              @click="togglePassword"
            ></i>
          </div>

          <div class="d-flex justify-content-between align-items-center my-3">
            <div class="form-check text-start">
              <input
                class="form-check-input"
                type="checkbox"
                v-model="rememberMe"
                id="flexCheckDefault"
              />
              <label class="form-check-label" for="flexCheckDefault">
                Remember me
              </label>
            </div>

            <div class="text-end">
              <RouterLink
                class="small text-decoration-none"
                :to="{ name: 'forgotPassword' }"
              >
                Forgot password?
              </RouterLink>
            </div>
          </div>

          <div class="d-grid gap-2 col-6 mx-auto mb-5">
            <button class="btn btn-success" type="submit" :disabled="loading">
              {{ loading ? "Signing In..." : "Sign In" }}
            </button>
            <!-- <RouterLink class="btn btn-danger" to="/app/apply">
              Sign up
            </RouterLink> -->
          </div>

          <hr />

          <p class="mt-3 mb-3 text-body-secondary">
            © 2022–{{ new Date().getFullYear() }}
          </p>
        </form>
      </main>
    </div>
  </div>
</template>

<style scoped>
html,
body {
  height: 100%;
}

.form-signin {
  max-width: 530px;
  padding: 1rem;
}

.form-signin .form-floating:focus-within {
  z-index: 2;
}

.form-signin input[type="number"] {
  margin-bottom: -1px;
  border-bottom-right-radius: 0;
  border-bottom-left-radius: 0;
}

.form-signin input[type="password"] {
  margin-bottom: 10px;
  border-top-left-radius: 0;
  border-top-right-radius: 0;
}
</style>
